/**
 * Requests per (template, person who raised it): GET /billing/request-breakdown.
 *
 * The Templates tab's "Raised by" and the People tab's "Templates used" are the
 * row and column sums of that one grid, which the server counts over the same
 * requests as the tables beside them. Everything here only adds cells up, so a
 * drill-down's rows always sum to its own total and its shares to 100.
 */
import { percentParts } from './apportion'

/** App-only sign-ins (the Outlook add-in, API clients) get a synthetic user with this suffix. */
export const SERVICE_ACCOUNT_SUFFIX = '@app.entra'

export function isServiceAccount(email) {
  return String(email || '').toLowerCase().endsWith(SERVICE_ACCOUNT_SUFFIX)
}

/** The server sends null for "no template" / "not recorded"; treat undefined the same. */
const same = (a, b) => (a ?? null) === (b ?? null)

/** What to call a person in a list. */
export function personName(user) {
  if (!user || user.kind === 'unknown' || (user.user_id ?? null) === null) return 'Not recorded'
  return user.username || user.email || 'Unknown user'
}

/** The line under the name. */
export function personDetail(user) {
  if (!user || user.kind === 'unknown' || (user.user_id ?? null) === null) {
    return 'Requests with no creator saved on them'
  }
  if (user.kind === 'service' || isServiceAccount(user.email)) return 'App sign-in (Outlook add-in or API), so no person is recorded'
  return user.email || ''
}

function withShares(rows) {
  const shares = percentParts(rows.map(r => r.requests))
  return rows.map((r, i) => ({ ...r, sharePct: shares[i] }))
}

const byCountThenName = name => (a, b) => b.requests - a.requests || name(a).localeCompare(name(b))

/**
 * Who raised the requests on one template: [{ ...user, requests, sharePct }],
 * busiest first, and their total.
 */
export function raisedBy(grid, templateId) {
  if (!grid) return { total: 0, rows: [] }
  const users = new Map((grid.users || []).map(u => [u.user_id ?? null, u]))
  const counts = new Map()
  for (const c of grid.cells || []) {
    if (!same(c.template_id, templateId)) continue
    const id = c.user_id ?? null
    counts.set(id, (counts.get(id) || 0) + c.requests)
  }
  const rows = [...counts].map(([id, requests]) => ({
    ...(users.get(id) || { user_id: id, kind: id === null ? 'unknown' : 'person' }),
    requests,
  })).sort(byCountThenName(personName))
  return { total: rows.reduce((s, r) => s + r.requests, 0), rows: withShares(rows) }
}

/**
 * The templates one person raised requests on: [{ template_id, template_name,
 * requests, sharePct }], busiest first, and their total.
 */
export function templatesUsed(grid, userId) {
  if (!grid) return { total: 0, rows: [] }
  const names = new Map((grid.templates || []).map(t => [t.template_id ?? null, t.template_name]))
  const counts = new Map()
  for (const c of grid.cells || []) {
    if (!same(c.user_id, userId)) continue
    const id = c.template_id ?? null
    counts.set(id, (counts.get(id) || 0) + c.requests)
  }
  const rows = [...counts].map(([id, requests]) => ({
    template_id: id,
    template_name: names.get(id) || (id === null ? 'No Template' : `Template #${id}`),
    requests,
  })).sort(byCountThenName(r => r.template_name))
  return { total: rows.reduce((s, r) => s + r.requests, 0), rows: withShares(rows) }
}

/**
 * Everyone not in `listedIds` (the People table's top rows), each with their
 * request total: what the "Everyone else" row is made of.
 */
export function everyoneElse(grid, listedIds = []) {
  if (!grid) return { total: 0, rows: [] }
  const listed = new Set(listedIds.map(id => id ?? null))
  const users = new Map((grid.users || []).map(u => [u.user_id ?? null, u]))
  const counts = new Map()
  for (const c of grid.cells || []) {
    const id = c.user_id ?? null
    if (listed.has(id)) continue
    counts.set(id, (counts.get(id) || 0) + c.requests)
  }
  const rows = [...counts].map(([id, requests]) => ({
    ...(users.get(id) || { user_id: id, kind: id === null ? 'unknown' : 'person' }),
    requests,
  })).sort(byCountThenName(personName))
  return { total: rows.reduce((s, r) => s + r.requests, 0), rows: withShares(rows) }
}

/**
 * The query a drill-down must use to count exactly the rows of the table it was
 * opened from: that table's own loaded period, or, for a trend the server cut
 * short (All time past 800 days), the window it actually covered.
 */
export function breakdownQuery(period, trendWindow = null) {
  if (trendWindow?.truncated && trendWindow.start && trendWindow.end) {
    return { range: 'custom', custom: { from: trendWindow.start, to: trendWindow.end } }
  }
  if (!period?.range) return null
  return period.range === 'custom' ? (period.custom ? period : null) : { range: period.range, custom: null }
}

export function breakdownKey(query) {
  if (!query) return null
  return query.custom ? `custom|${query.custom.from}|${query.custom.to}` : query.range
}
